import L from 'leaflet';
import 'leaflet.markercluster';

// Cluster markers are display-only, including those created internally by the plugin.
L.MarkerCluster.mergeOptions({ pmIgnore: true, snapIgnore: true });

type Listing = { id: number; latitude?: number | null; longitude?: number | null };
type Entry<T> = { marker: L.Marker; items: T[]; count: number };

/** Coordinate grouping plus cancellable batches; no search or geometry ownership. */
export class ClusteredListingMarkers<T extends Listing> {
  private icon?: L.Icon;
  private readonly entries = new Map<string, Entry<T>>();
  private readonly counts = new WeakMap<L.Marker, number>();
  private timer?: ReturnType<typeof setTimeout>;
  readonly layer = L.markerClusterGroup({
    removeOutsideVisibleBounds: true,
    showCoverageOnHover: false,
    // Batch here instead of plugin timers so replacement/teardown cancels pending work.
    chunkedLoading: false,
    animateAddingMarkers: false,
    spiderLegPolylineOptions: { pmIgnore: true, snapIgnore: true, weight: 1.5, color: '#555' } as L.PolylineOptions,
    iconCreateFunction: cluster => {
      const count = cluster.getAllChildMarkers().reduce((sum, marker) => sum + (this.counts.get(marker) ?? 1), 0);
      return L.divIcon({ html: '<span>' + count + '</span>', className: 'bazar-marker-cluster', iconSize: [40, 40] });
    },
  });

  constructor(private readonly click: (items: T[], marker: L.Marker) => void) {}

  /** Refresh listing metadata without touching marker icons, layers or pending batches. */
  updateData(items: readonly T[], icon: L.Icon): boolean {
    if (this.icon !== icon) return false;
    const groups = new Map<string, T[]>();
    for (const item of items) {
      if (!Number.isFinite(item.latitude) || !Number.isFinite(item.longitude)) continue;
      const key = item.latitude + ',' + item.longitude;
      const group = groups.get(key) ?? [];
      group.push(item);
      groups.set(key, group);
    }
    if (groups.size !== this.entries.size) return false;
    for (const [key, group] of groups) {
      const entry = this.entries.get(key);
      if (!entry || entry.items.length !== group.length ||
          !group.every(item => entry.items.some(previous => previous.id === item.id))) return false;
    }
    for (const [key, group] of groups) this.entries.get(key)!.items = group;
    return true;
  }

  reconcile(items: readonly T[], icon: L.Icon): Map<number, L.Marker> {
    this.icon = icon;
    clearTimeout(this.timer);
    const groups = new Map<string, T[]>();
    for (const item of items) {
      if (!Number.isFinite(item.latitude) || !Number.isFinite(item.longitude)) continue;
      const key = item.latitude + ',' + item.longitude;
      const group = groups.get(key) ?? [];
      group.push(item); groups.set(key, group);
    }
    const removed: L.Marker[] = [];
    for (const [key, entry] of this.entries) {
      if (!groups.has(key)) { removed.push(entry.marker); entry.marker.off(); this.entries.delete(key); }
    }
    this.layer.removeLayers(removed);
    const singles = new Map<number, L.Marker>();
    const pending: L.Marker[] = [];
    const changed: L.Marker[] = [];
    for (const [key, group] of groups) {
      let entry = this.entries.get(key);
      if (!entry) {
        const marker = L.marker([group[0].latitude!, group[0].longitude!], { pmIgnore: true, snapIgnore: true, keyboard: true });
        entry = { marker, items: group, count: 0 };
        const current = entry;
        marker.on('click', event => { L.DomEvent.stopPropagation(event); this.click(current.items, marker); });
        this.entries.set(key, entry);
      }
      // Always replace data references, even when membership/count is unchanged.
      entry.items = group;
      this.counts.set(entry.marker, group.length);
      if (entry.count !== group.length || (group.length === 1 && entry.marker.options.icon !== icon)) {
        entry.marker.setIcon(group.length === 1 ? icon : L.divIcon({
          html: '<div class="group-marker">' + group.length + '</div>',
          className: 'group-marker-wrapper', iconSize: [32, 32], iconAnchor: [16, 16],
        }));
        entry.count = group.length; changed.push(entry.marker);
      }
      if (group.length === 1) singles.set(group[0].id, entry.marker);
      if (!this.layer.hasLayer(entry.marker)) pending.push(entry.marker);
    }
    if (changed.length) this.layer.refreshClusters(changed);
    // Yield between small batches; a new result or destroy cancels the old queue.
    let offset = 0;
    const addBatch = () => {
      this.layer.addLayers(pending.slice(offset, offset + 100));
      offset += 100;
      if (offset < pending.length) this.timer = setTimeout(addBatch, 0);
    };
    if (pending.length) addBatch();
    return singles;
  }

  destroy(): void {
    clearTimeout(this.timer);
    for (const entry of this.entries.values()) entry.marker.off();
    this.layer.clearLayers(); this.entries.clear();
  }
}
